import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { isEmpty } from '../utils/common.utils';

/**
 * Service for managing responsive font sizing based on viewport width
 * Dynamically adjusts root font size to scale with window dimensions
 * Maintains minimum and maximum font size constraints
 */
@Injectable({
  providedIn: 'root',
})
export class ResponsiveFontService {
  /**
   * Default base width for scaling calculations (1920px desktop)
   * Used as reference point for responsive calculations
   */
  private readonly DEFAULT_BASE_WIDTH = 1920;

  /**
   * Default base font size for scaling calculations (16px standard)
   * Represents the font size at base width
   */
  private readonly DEFAULT_BASE_FONT_SIZE = 16;

  /**
   * Default minimum font size to prevent text from becoming too small (12px)
   * Ensures readability on very small screens
   */
  private readonly DEFAULT_MIN_FONT_SIZE = 12;

  /**
   * Default maximum font size to prevent text from becoming too large (32px)
   * Ensures reasonable scaling on very large displays
   */
  private readonly DEFAULT_MAX_FONT_SIZE = 32;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  /**
   * Sets responsive font size on document root element
   * Calculates font size based on current viewport width relative to base width
   * Applies minimum and maximum font size constraints
   * Safely applies styles with error handling
   *
   * @param baseWidth - Reference width for scaling calculations (default: 1920px)
   * @param baseFontSize - Font size at base width (default: 16px)
   * @param minFontSize - Minimum allowed font size (default: 12px)
   * @param maxFontSize - Maximum allowed font size (default: 32px)
   * @returns void
   * @throws Never throws - all errors logged with graceful fallback
   */
  setResponsiveFontSize(
    baseWidth: number = this.DEFAULT_BASE_WIDTH,
    baseFontSize: number = this.DEFAULT_BASE_FONT_SIZE,
    minFontSize: number = this.DEFAULT_MIN_FONT_SIZE,
    maxFontSize: number = this.DEFAULT_MAX_FONT_SIZE
  ): void {
    try {
      if (!this.validateInputs(baseWidth, baseFontSize, minFontSize, maxFontSize)) {
        this.applyDefaultFontSize();
        return;
      }

      const currentWidth = this.getCurrentViewportWidth();
      const calculatedFontSize = this.calculateResponsiveFontSize(
        currentWidth,
        baseWidth,
        baseFontSize,
        minFontSize,
        maxFontSize
      );

      this.applyFontSizeToRoot(calculatedFontSize);
    } catch (error) {
      console.error('Error in setResponsiveFontSize:', error);
      this.applyDefaultFontSize();
    }
  }

  /**
   * Validates all input parameters for responsive font sizing
   * Ensures all values are positive numbers and within reasonable ranges
   * @param baseWidth - Base width value to validate
   * @param baseFontSize - Base font size value to validate
   * @param minFontSize - Minimum font size value to validate
   * @param maxFontSize - Maximum font size value to validate
   * @returns True if all parameters are valid, false otherwise
   */
  private validateInputs(
    baseWidth: number,
    baseFontSize: number,
    minFontSize: number,
    maxFontSize: number
  ): boolean {
    try {
      if (isEmpty(baseWidth) || isEmpty(baseFontSize) || isEmpty(minFontSize)) {
        console.warn('validateInputs: required parameters are empty');
        return false;
      }

      if (typeof baseWidth !== 'number' || typeof baseFontSize !== 'number') {
        console.warn('validateInputs: parameters must be numbers');
        return false;
      }

      if (baseWidth <= 0 || baseFontSize <= 0 || minFontSize <= 0) {
        console.warn('validateInputs: all sizes must be positive');
        return false;
      }

      if (minFontSize > maxFontSize) {
        console.warn('validateInputs: minFontSize cannot exceed maxFontSize');
        return false;
      }

      if (minFontSize > baseFontSize) {
        console.warn('validateInputs: minFontSize cannot exceed baseFontSize');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in validateInputs:', error);
      return false;
    }
  }

  /**
   * Gets current viewport width
   * Handles edge cases where window.innerWidth is unavailable
   * @returns Current window width or fallback value
   */
  private getCurrentViewportWidth(): number {
    try {
      if (isEmpty(window)) {
        console.warn('getCurrentViewportWidth: window object not available');
        return this.DEFAULT_BASE_WIDTH;
      }

      const width = window.innerWidth;

      if (isEmpty(width) || width <= 0) {
        console.warn('getCurrentViewportWidth: invalid window width');
        return this.DEFAULT_BASE_WIDTH;
      }

      return width;
    } catch (error) {
      console.error('Error in getCurrentViewportWidth:', error);
      return this.DEFAULT_BASE_WIDTH;
    }
  }

  /**
   * Calculates responsive font size with scaling and constraints
   * Formula: newFontSize = baseFontSize * (currentWidth / baseWidth)
   * Then clamps value between minFontSize and maxFontSize
   * @param currentWidth - Current viewport width
   * @param baseWidth - Reference width for calculations
   * @param baseFontSize - Base font size to scale
   * @param minFontSize - Minimum allowed font size
   * @param maxFontSize - Maximum allowed font size
   * @returns Calculated and constrained font size value
   */
  private calculateResponsiveFontSize(
    currentWidth: number,
    baseWidth: number,
    baseFontSize: number,
    minFontSize: number,
    maxFontSize: number
  ): number {
    try {
      if (isEmpty(currentWidth) || isEmpty(baseWidth) || isEmpty(baseFontSize)) {
        console.warn('calculateResponsiveFontSize: required values are empty');
        return this.DEFAULT_BASE_FONT_SIZE;
      }

      const scale = currentWidth / baseWidth;
      let newFontSize = baseFontSize * scale;

      if (newFontSize < minFontSize) {
        newFontSize = minFontSize;
      }

      if (newFontSize > maxFontSize) {
        newFontSize = maxFontSize;
      }

      return newFontSize;
    } catch (error) {
      console.error('Error in calculateResponsiveFontSize:', error);
      return this.DEFAULT_BASE_FONT_SIZE;
    }
  }

  /**
   * Applies calculated font size to document root element
   * Sets CSS style on html element for global font scaling
   * @param fontSize - Font size value to apply
   * @returns void
   */
  private applyFontSizeToRoot(fontSize: number): void {
    try {
      if (isEmpty(fontSize) || isEmpty(this.document) || isEmpty(this.document.documentElement)) {
        console.warn('applyFontSizeToRoot: document element not available');
        return;
      }

      const validFontSize = Math.max(this.DEFAULT_MIN_FONT_SIZE, Math.min(fontSize, this.DEFAULT_MAX_FONT_SIZE));
      this.document.documentElement.style.fontSize = `${validFontSize}px`;
    } catch (error) {
      console.error('Error in applyFontSizeToRoot:', error);
    }
  }

  /**
   * Applies default font size to document root element
   * Used as fallback when calculations fail
   * @returns void
   */
  private applyDefaultFontSize(): void {
    try {
      if (!isEmpty(this.document) && !isEmpty(this.document.documentElement)) {
        this.document.documentElement.style.fontSize = `${this.DEFAULT_BASE_FONT_SIZE}px`;
      }
    } catch (error) {
      console.error('Error in applyDefaultFontSize:', error);
    }
  }

  /**
   * Resets font size to default value (16px)
   * Removes responsive scaling and applies standard size
   * @returns void
   */
  resetFontSize(): void {
    try {
      this.applyDefaultFontSize();
    } catch (error) {
      console.error('Error in resetFontSize:', error);
    }
  }

  /**
   * Gets current root font size from document
   * Reads computed font size on html element
   * @returns Current font size value or default if unavailable
   */
  getCurrentFontSize(): number {
    try {
      if (isEmpty(this.document) || isEmpty(this.document.documentElement)) {
        console.warn('getCurrentFontSize: document element not available');
        return this.DEFAULT_BASE_FONT_SIZE;
      }

      const computedStyle = window.getComputedStyle(this.document.documentElement);
      const fontSizeStr = computedStyle.fontSize;

      if (isEmpty(fontSizeStr)) {
        console.warn('getCurrentFontSize: computed font size is empty');
        return this.DEFAULT_BASE_FONT_SIZE;
      }

      const fontSize = parseFloat(fontSizeStr);

      if (Number.isNaN(fontSize) || fontSize <= 0) {
        console.warn('getCurrentFontSize: invalid font size value');
        return this.DEFAULT_BASE_FONT_SIZE;
      }

      return fontSize;
    } catch (error) {
      console.error('Error in getCurrentFontSize:', error);
      return this.DEFAULT_BASE_FONT_SIZE;
    }
  }
}

